<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { onClickOutside } from '@vueuse/core'

import AppIcon from '@/components/ui/AppIcon.vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { initials } from '@/utils/format'

const { t } = useI18n()
const auth = useAuthStore()
const ui = useUiStore()
const router = useRouter()
const roleLabel = computed(() => {
  if (
    auth.role === 'learner' &&
    auth.authProvider === 'reybex' &&
    auth.reybexAccount?.isPartnerAccount === true
  ) return 'PARTNER LEARNER'
  return auth.role ? t(`common.${auth.role}`) : ''
})

// A Vue-native menu rather than a Preline dropdown: it has to react to the
// auth store, and re-initialising a Preline instance on every session change
// is more moving parts than this.
const open = ref(false)
const root = ref<HTMLElement | null>(null)
onClickOutside(root, () => (open.value = false))

async function signOut() {
  open.value = false
  await auth.signOut()
  ui.info(t('auth.signedOut'))
  await router.push({ name: 'login' })
}
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      class="flex items-center gap-x-2 rounded-lg p-1.5 text-sm hover:bg-gray-100 dark:hover:bg-neutral-700"
      :aria-expanded="open"
      aria-haspopup="menu"
      @click="open = !open"
    >
      <span
        class="flex size-8 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-900 dark:text-brand-200"
        aria-hidden="true"
      >
        {{ initials(auth.displayName) }}
      </span>
      <span class="hidden max-w-32 truncate text-gray-700 sm:block dark:text-neutral-300">
        {{ auth.displayName }}
      </span>
      <AppIcon name="chevron-down" size="sm" class="text-gray-400" />
    </button>

    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="scale-95 opacity-0"
      leave-active-class="transition duration-75 ease-in"
      leave-to-class="scale-95 opacity-0"
    >
      <div
        v-if="open"
        role="menu"
        class="absolute end-0 z-40 mt-2 w-56 origin-top-right rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-gray-200 dark:bg-neutral-800 dark:ring-neutral-700"
      >
        <div class="border-b border-gray-100 px-4 py-2 dark:border-neutral-700">
          <p class="truncate text-sm font-medium text-gray-900 dark:text-white">
            {{ auth.displayName }}
          </p>
          <p class="truncate text-xs text-gray-500 dark:text-neutral-400">
            {{ auth.profile?.email }}
          </p>
          <p class="mt-1 text-[11px] uppercase tracking-wide text-brand-600 dark:text-brand-400">
            {{ roleLabel }}
          </p>
        </div>

        <RouterLink
          to="/profile"
          role="menuitem"
          class="flex items-center gap-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-neutral-700"
          @click="open = false"
        >
          <AppIcon name="user" size="sm" />
          {{ t('nav.profile') }}
        </RouterLink>

        <button
          type="button"
          role="menuitem"
          class="flex w-full items-center gap-x-2 border-t border-gray-100 px-4 py-2 text-start text-sm text-red-600 hover:bg-red-50 dark:border-neutral-700 dark:hover:bg-red-950"
          @click="signOut"
        >
          <AppIcon name="log-out" size="sm" />
          {{ t('common.signOut') }}
        </button>
      </div>
    </Transition>
  </div>
</template>
